import { useSettingsHeader } from '@pages/Settings/SettingsHeader.context'
import { CommunicationFlowType, ICallFlow } from '@dataTypes/clinic.type'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import {
  deleteCallFlowAction,
  fetchCallFlowsAction,
  fetchPhoneManagementData,
  updateCallFlowAction,
} from '@store/actions/settings-communication.action'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { useSettingsSubpageLoading } from '@pages/Settings/SettingsSubpageGate.component'
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import useClinicChange from '../shared/useClinicChange'
import type { FilterSelection } from '@enamelai/design-system'

const LOADING_CONTAINER = 'settings-call-flow'

export type CallFlowMode = 'list' | 'editor'

export interface ICallFlowHooks {
  mode: CallFlowMode
  callFlows: ICallFlow[]
  cancelEditor: () => void
  deleteFlow: (flow: ICallFlow) => Promise<void>
  deleteFlowConfirm: ICallFlow | null
  draft: ICallFlow | null
  filters: FilterSelection
  filteredCallFlows: ICallFlow[]
  loading: boolean
  dataReady: boolean
  mainSearchQuery: string
  setDeleteFlowConfirm: (flow: ICallFlow | null) => void
  setFilters: React.Dispatch<React.SetStateAction<FilterSelection>>
  setHeaderButtons: (n: ReactNode | null) => void
  setHeaderTitle: (n: ReactNode | null) => void
  setMainSearchQuery: React.Dispatch<React.SetStateAction<string>>
  setSwitchFlowConfirm: (flow: ICallFlow | null) => void
  selectFlowTypeOpen: boolean
  setSelectFlowTypeOpen: (open: boolean) => void
  startCreate: (flowType: CommunicationFlowType) => void
  startEdit: (flow: ICallFlow) => void
  switchFlowConfirm: ICallFlow | null
  updateCallFlowStatus: (
    flow: ICallFlow,
    status: 'active' | 'inactive',
  ) => Promise<void>
}

const useCallFlow = (): ICallFlowHooks => {
  const dispatch = useAppDispatch()
  const { setHeaderButtons, setHeaderTitle, setShowSidebar } =
    useSettingsHeader()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()
  const { callFlows, loading } = useAppSelector((s) => s.settingsCommunication)
  const selectedClinicId = useAppSelector(
    (s) => s.clinic.selectedClinic?.clinic_id ?? '',
  )
  const clinicId =
    selectedClinicId ||
    (typeof localStorage !== 'undefined'
      ? localStorage.getItem('clinic_id') || ''
      : '')

  const [mode, setMode] = useState<CallFlowMode>('list')
  const [draft, setDraft] = useState<ICallFlow | null>(null)
  const [selectFlowTypeOpen, setSelectFlowTypeOpen] = useState(false)
  const [deleteFlowConfirm, setDeleteFlowConfirm] = useState<ICallFlow | null>(
    null,
  )
  const [switchFlowConfirm, setSwitchFlowConfirm] = useState<ICallFlow | null>(
    null,
  )
  const [mainSearchQuery, setMainSearchQuery] = useState('')
  const [filters, setFilters] = useState<FilterSelection>({})
  const [dataReady, setDataReady] = useState(false)
  const loadGen = useRef(0)

  useClinicChange(clinicId, () => {
    loadGen.current += 1
    setDataReady(false)
    setMode('list')
    setDraft(null)
  })

  useSettingsSubpageLoading(!dataReady)

  const filteredCallFlows = useMemo(() => {
    const query = mainSearchQuery.trim().toLowerCase()
    const statusSel = filters.status ?? []

    return callFlows.filter((f) => {
      if (statusSel.length > 0) {
        const s = (f.status ?? '').toLowerCase()
        if (!statusSel.some((label) => s === label.toLowerCase())) {
          return false
        }
      }
      if (!query) return true
      const namePart = (f.name ?? '').toLowerCase()
      const numbersPart = (f.number_links ?? [])
        .map((l) => {
          const pn = l.clinic_number?.phone_number ?? ''
          const id = l.clinic_number_id ?? ''
          return `${pn} ${id}`
        })
        .join(' ')
        .toLowerCase()
      const haystack = `${namePart} ${numbersPart}`
      return haystack.includes(query)
    })
  }, [callFlows, mainSearchQuery, filters])

  const refreshList = useCallback(async () => {
    const gen = ++loadGen.current
    if (!clinicId) {
      setDataReady(true)
      return
    }
    try {
      await dispatch(fetchPhoneManagementData(clinicId)).unwrap()
      await dispatch(fetchCallFlowsAction(clinicId)).unwrap()
    } catch {
      showToast(createFailedToast('Error', 'Could not load call flows.'))
    } finally {
      if (loadGen.current === gen) setDataReady(true)
    }
  }, [clinicId, dispatch, showToast])

  useEffect(() => {
    void refreshList()
  }, [refreshList])

  const startCreate = useCallback(
    (flowType: CommunicationFlowType) => {
      setSelectFlowTypeOpen(false)
      setDraft({
        clinic_id: clinicId,
        name: 'New flow',
        status: 'draft',
        flow_type: flowType,
        definition: {
          version: 1,
          numbers: [],
          numberSettings: {},
          branches: {
            work: { steps: [] },
            lunch: { steps: [] },
            closed: { steps: [] },
          },
        },
      })
      setMode('editor')
    },
    [clinicId],
  )

  const startEdit = useCallback((flow: ICallFlow) => {
    setDraft(flow)
    setMode('editor')
  }, [])

  const cancelEditor = useCallback(() => {
    setDraft(null)
    setMode('list')
  }, [])

  useEffect(() => {
    if (mode === 'editor') {
      setShowSidebar(false)
    } else {
      setShowSidebar(true)
    }
  }, [mode])

  const deleteFlow = useCallback(
    async (flow: ICallFlow) => {
      if (!flow.call_flow_id) return
      showLoading({ container: LOADING_CONTAINER })
      try {
        await dispatch(deleteCallFlowAction(flow.call_flow_id)).unwrap()
        showToast(createSuccessToast('Deleted', 'Call flow removed.'))
        await refreshList()
      } catch {
        showToast(createFailedToast('Error', 'Could not delete call flow.'))
      } finally {
        hideLoading(LOADING_CONTAINER)
      }
    },
    [dispatch, hideLoading, refreshList, showLoading, showToast],
  )

  const updateCallFlowStatus = useCallback(
    async (flow: ICallFlow, status: 'active' | 'inactive') => {
      if (!flow.call_flow_id) return
      showLoading({ container: LOADING_CONTAINER })
      try {
        await dispatch(
          updateCallFlowAction({
            id: flow.call_flow_id,
            body: { status },
          }),
        ).unwrap()
        showToast(
          createSuccessToast(
            'Updated',
            status === 'active'
              ? 'Call flow is now active.'
              : 'Call flow is now inactive.',
          ),
        )
        await refreshList()
      } catch {
        showToast(
          createFailedToast('Error', 'Could not update call flow status.'),
        )
      } finally {
        hideLoading(LOADING_CONTAINER)
        setSwitchFlowConfirm(null)
      }
    },
    [dispatch, hideLoading, refreshList, showLoading, showToast],
  )

  return {
    mode,
    callFlows,
    cancelEditor,
    deleteFlowConfirm,
    deleteFlow,
    draft,
    filters,
    filteredCallFlows,
    loading,
    dataReady,
    mainSearchQuery,
    setDeleteFlowConfirm,
    setFilters,
    setHeaderButtons,
    setHeaderTitle,
    setMainSearchQuery,
    setSwitchFlowConfirm,
    selectFlowTypeOpen,
    setSelectFlowTypeOpen,
    startCreate,
    startEdit,
    switchFlowConfirm,
    updateCallFlowStatus,
  }
}

export default useCallFlow
